import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import Image from 'components/image'
import { InfoButton } from 'components/buttons'
import { numberToCurrency } from 'lib/money'
import { reservationsPath } from 'lib/urls'
// @ts-ignore
import { track, useTracking } from 'lib/analytics'
import ExternalProductOffer from './ExternalProductOffer'
// @ts-ignore
import styles from './BabylistOffer.scss'

interface BabylistOfferWithDeliveryEstimatesProps {
  price: number
  regItem: any
  registry: any
  onClick: () => void
  inCart: boolean
  trackedCreateReservation: () => void
}

function useIsInViewport(ref: any) {
  const [isIntersecting, setIsIntersecting] = useState(false)

  const observer = useMemo(
    () =>
      new IntersectionObserver(([entry]) =>
        setIsIntersecting(entry.isIntersecting)
      ),
    []
  )

  useEffect(() => {
    observer.observe(ref.current)

    return () => {
      observer.disconnect()
    }
  }, [ref, observer])

  return isIntersecting
}

const getFreeShippingElement = (regItem: any) => {
  if (!regItem.price) return null

  if (regItem.price >= 45) {
    return (
      <div className={styles.BabylistOffer__freeShipOver45}>
        <span>Free shipping* 🚚</span>
        <span>Free returns 💗</span>
      </div>
    )
  }

  return (
    <div className={styles.BabylistOffer__freeShip}>
      <span>Free shipping on items over $45* 🚚</span>
      <span>Free returns 💗</span>
    </div>
  )
}

// eslint-disable-next-line complexity
export const BabylistOfferWithDeliveryEstimates = ({
  price,
  regItem,
  registry,
  onClick,
  inCart,
  trackedCreateReservation,
}: BabylistOfferWithDeliveryEstimatesProps) => {
  const [isInViewportEventSent, setIsInViewportEventSent] = useState(false)
  const tracker = useTracking()
  const elRef = useRef(null)
  const isInViewport = useIsInViewport(elRef)

  const { isAvailableOnBabylist, offers } = regItem

  const showDeliveryEstimates =
    isAvailableOnBabylist &&
    !!regItem.deliveryEstimate &&
    !regItem.isElectronicGiftCard

  const formattedDeliveryEstimate = () => {
    if (!regItem.deliveryEstimate?.message) return null

    const deliveryEstimateSplit = regItem?.deliveryEstimate?.message?.split('-')
    const min = deliveryEstimateSplit[0]
    const max = deliveryEstimateSplit[1]
    return (
      <>
        <span>{min} - </span>
        <span className={styles.BabylistOffer__nowrap}>{max}</span>
      </>
    )
  }

  const handleAddToCart = useCallback(() => {
    if (typeof onClick !== undefined) {
      onClick()
    }

    tracker.trackEvent({
      event: track.registryProductOfferClicked,
      store: 'Babylist',
    })
  }, [onClick])

  if (
    !isInViewportEventSent &&
    isInViewport &&
    !!regItem?.deliveryEstimate?.message
  ) {
    tracker.trackEvent({
      event: track.registryItemDeliveryEstimateViewed,
      registry,
      regItemId: regItem.id,
      deliveryEstimate: regItem.deliveryEstimate.message,
      willArriveBeforeShower: regItem.deliveryEstimate.arrivesBeforeShower,
      minEstimate: regItem.deliveryEstimate.min,
      maxEstimate: regItem.deliveryEstimate.max,
    })
    setIsInViewportEventSent(true)
  }

  return (
    <div className={styles.BabylistOffer} ref={elRef}>
      <div className={styles.BabylistOffer__top}>
        <div className={styles.BabylistOffer__shopHeader}>
          <div className={styles.BabylistOffer__logo}>
            <Image
              src="//images.babylist.com/image/upload/v1631884941/bl_store_icon_o8u8vu.png"
              width={30}
            />
            <div className={styles.BabylistOffer__title}>Babylist Shop</div>
          </div>
          <div className={styles.BabylistOffer__price}>
            {numberToCurrency(price)}
          </div>
        </div>

        {showDeliveryEstimates && (
          <div className={styles.BabylistOffer__estimate}>
            <span className={styles.BabylistOffer__estimateTitle}>
              Estimated Arrival{' '}
              <Image
                src="//images.babylist.com/image/upload/v1680540311/Icon_Truck_uvjchg.svg"
                width={22}
              />
            </span>
            <span className={styles.BabylistOffer__estimateDate}>
              {formattedDeliveryEstimate()}
            </span>
            {regItem.deliveryEstimate.arrivesBeforeShower && (
              <span className={styles.BabylistOffer__estimateShower}>
                This gift will be delivered before the Baby Shower
              </span>
            )}
          </div>
        )}

        <div>
          {!isAvailableOnBabylist && (
            <small className={styles.BabylistOffer__outOfStock}>
              Item is currently out of stock
            </small>
          )}

          {inCart ? (
            <InfoButton
              block
              empty
              pill
              className="mtm pam"
              href={reservationsPath}
            >
              In Cart
            </InfoButton>
          ) : (
            <>
              <InfoButton
                block
                pill
                className="mtm pam"
                disabled={!isAvailableOnBabylist}
                onClick={handleAddToCart}
              >
                Add to Cart
              </InfoButton>

              {getFreeShippingElement(regItem)}
            </>
          )}
        </div>
      </div>
      {offers ? (
        <div className={styles.BabylistOffer__offers}>
          <ExternalProductOffer
            offers={offers}
            regItem={regItem}
            registry={registry}
            trackedCreateReservation={trackedCreateReservation}
          />
        </div>
      ) : null}
    </div>
  )
}
